import React, {useState, useCallback} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import _ from 'lodash';

import Intercom from '@intercom/messenger-js-sdk';

// MUI
import {AppBar, Box, IconButton, Toolbar} from '@mui/material';

// Icons
import Logo from '../../assets/logo.svg';
import HelpCircleIcon from '../../assets/help-circle.svg';
import NotificationDotIcon from '../../assets/red-dot.svg';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import {
  useAuthInfo,
  useLogoutFunction,
  useRedirectFunctions,
} from '@propelauth/react';
import {useSelector} from 'react-redux';
import {APP_HEADER_HEIGHT, GREEN_NAV} from '../../App';
import {getSharedDocumentData} from '../../util/sharedDocumentStorage';
import TruncatedTooltip from '../common/TruncatedTooltip';
import {UserAvatar} from './_UserAvatar';
import {UserMenu} from './_UserMenu';
import {ResourcesMenu} from './_ResourcesMenu';
import {NavBreadcrumbs} from './_NavBreadcrumbs';
import {WHITE_80} from '../../theme';
import {useNavigationGuard} from '../../context/NavigationGuardContext';
import {useHeadwayWidget} from '../../hooks/useHeadwayWidget';

export const AppBarV2 = React.memo(() => {
  const {user} = useAuthInfo();
  const {documentId} = useParams();
  const navigate = useNavigate();
  const {runGuarded} = useNavigationGuard();

  if (!user) return null;
  const {currentOrg, orgs, hasParentOrg, isShareLinkMode, isExternalUser} =
    useSelector((state) => state.session);
  const logoutFn = useLogoutFunction();
  const location = useLocation();
  const {redirectToLoginPage} = useRedirectFunctions();
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElResources, setAnchorElResources] = useState(null);

  const {unseenCount: changelogUnseenCount, markSeen: markChangelogSeen} =
    useHeadwayWidget();

  const parentOrg = orgs.find((org) => org.orgMetadata?.isParentOrganization);
  const isParentOrgPage =
    location.pathname === '/parent-organization' || location.pathname === '/';
  const hideOrgSwitcher = hasParentOrg && isParentOrgPage;
  const isSettingsPage = location.pathname.startsWith('/settings');

  // Get org name from shared document data if in share mode
  const sharedDocumentData = isShareLinkMode ? getSharedDocumentData() : null;
  const shareLinkOrgName = sharedDocumentData?.orgName?.toUpperCase();

  const orgAppBarTitle =
    shareLinkOrgName ||
    (hideOrgSwitcher
      ? parentOrg?.orgName?.toUpperCase()
      : currentOrg?.orgName?.toUpperCase());

  const handleOpenUserMenu = useCallback(
    (event) => {
      const target = event.currentTarget;
      runGuarded(() => setAnchorElUser(target));
    },
    [runGuarded]
  );

  const handleCloseUserMenu = useCallback(() => {
    setAnchorElUser(null);
  }, []);

  const handleOpenResourcesMenu = useCallback((event) => {
    setAnchorElResources(event.currentTarget);
  }, []);

  const handleCloseResourcesMenu = useCallback(() => {
    setAnchorElResources(null);
  }, []);

  const handleLogoClick = useCallback(
    (e) => {
      if (isExternalUser) return;
      e.preventDefault();
      runGuarded(() => navigate('/'));
    },
    [isExternalUser, runGuarded, navigate]
  );

  const handleMenuClick = useCallback(
    async (item) => {
      handleCloseUserMenu();
      handleCloseResourcesMenu();

      if (item === 'changelog') {
        markChangelogSeen();
      } else if (item === 'logout') {
        Intercom('shutdown');
        await logoutFn(false);
        redirectToLoginPage({postLoginRedirectUrl: window.location.href});
      }
    },
    [
      handleCloseUserMenu,
      handleCloseResourcesMenu,
      markChangelogSeen,
      logoutFn,
      redirectToLoginPage,
    ]
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        height: APP_HEADER_HEIGHT,
        zIndex: (theme) => theme.zIndex.drawer,
        boxShadow: 0,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          height: '100%',
          minHeight: APP_HEADER_HEIGHT + '!important',
          backgroundColor: GREEN_NAV,
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          onClick={handleLogoClick}
          sx={{
            width: '120px',
            height: '100%',
            pl: '12px',
            cursor: isExternalUser ? 'default' : 'pointer',
            textDecoration: 'none',
          }}
        >
          <img src={Logo} alt="Logo" width={88} height={27} />
        </Box>

        <Box pl={2}>
          {(location.pathname === '/document' ||
            (_.startsWith(location.pathname, '/documents/') &&
              !_.isNil(documentId)) ||
            location.pathname.startsWith('/disclosure-library/') ||
            location.pathname.startsWith('/rollforward-walk-through/')) && (
            <NavBreadcrumbs />
          )}
        </Box>

        <Box flexGrow={1} />

        {!isSettingsPage && (
          <Box sx={{flexGrow: 0, mr: 2}}>
            <Box display="flex" flexDirection="row" alignItems="center">
              <IconButton
                onClick={handleOpenResourcesMenu}
                sx={{mr: 2, p: 0.5}}
              >
                <Box position="relative">
                  <img
                    src={HelpCircleIcon}
                    height={26}
                    width={26}
                    style={{
                      display: 'block',
                      filter: 'brightness(0) invert(1)',
                    }}
                  />
                  {changelogUnseenCount > 0 && (
                    <Box
                      component="img"
                      src={NotificationDotIcon}
                      sx={{
                        height: 10,
                        position: 'absolute',
                        right: -2,
                        top: 0,
                        width: 10,
                      }}
                    />
                  )}
                </Box>
              </IconButton>

              <TruncatedTooltip title={orgAppBarTitle}>
                <Typography
                  color={WHITE_80}
                  noWrap
                  sx={{
                    pr: 2,
                    fontWeight: 600,
                    fontSize: 14,
                    maxWidth: 'clamp(800px, 15vw, 500px)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'default',
                  }}
                >
                  {orgAppBarTitle}
                </Typography>
              </TruncatedTooltip>

              <IconButton onClick={handleOpenUserMenu} sx={{p: 0}}>
                <UserAvatar
                  user={user}
                  bgColor="#DEF1E9"
                  textColor={GREEN_NAV}
                  size={24}
                />
                <ExpandMoreIcon
                  sx={{
                    color: WHITE_80,
                    width: 12,
                    height: 12,
                    ml: 0.5,
                  }}
                />
              </IconButton>
            </Box>

            <UserMenu
              anchorEl={anchorElUser}
              onClose={handleCloseUserMenu}
              onMenuClick={handleMenuClick}
            />
            <ResourcesMenu
              anchorEl={anchorElResources}
              changelogUnseenCount={changelogUnseenCount}
              onClose={handleCloseResourcesMenu}
              onMenuClick={handleMenuClick}
            />
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
});

AppBarV2.displayName = 'AppBarV2';
